import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import {
  ChartIcon,
  CloudOffIcon,
  PeopleIcon,
  ScheduleIcon,
  SettingsIcon,
  TodayIcon,
} from '@/client/components/icons';
import { Spinner } from '@/client/components/ui';
import { useIsOnline, usePendingWrites } from '@/client/store';

/**
 * Navigation: a thumb-reachable bar along the bottom on phones, a sidebar on
 * anything wider. Both render from the same list so they cannot drift.
 */

const NAV = [
  { to: '/', label: 'Today', Icon: TodayIcon },
  { to: '/schedule', label: 'Schedule', Icon: ScheduleIcon },
  { to: '/volunteers', label: 'Volunteers', Icon: PeopleIcon },
  { to: '/reports', label: 'Reports', Icon: ChartIcon },
  { to: '/settings', label: 'Settings', Icon: SettingsIcon },
] as const;

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return (
    <div className="min-h-dvh md:flex">
      <nav
        aria-label="Main"
        className="hidden w-56 shrink-0 border-r border-border bg-surface-raised md:block"
      >
        <div className="sticky top-0 flex flex-col gap-1 p-3">
          <p className="flex items-center gap-2 px-3 py-4 text-sm font-semibold tracking-tight">
            <img src="/logo.svg" alt="" className="h-7 w-auto" />
            Kingdom Haul
          </p>
          {NAV.map(({ to, label, Icon }) => (
            <Link
              key={to}
              to={to}
              className={`tap-target flex items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors ${
                isActive(pathname, to)
                  ? 'bg-brand-soft text-brand-ink'
                  : 'text-ink-muted hover:bg-surface-sunken'
              }`}
            >
              <Icon className="size-5" />
              {label}
            </Link>
          ))}
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <ConnectionBanner />
        {/* Bottom padding clears the mobile tab bar. */}
        <main className="flex-1 pb-24 md:pb-8">{children}</main>
      </div>

      <nav
        aria-label="Main"
        className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface-raised/95 backdrop-blur md:hidden"
      >
        <ul className="grid grid-cols-5">
          {NAV.map(({ to, label, Icon }) => {
            const active = isActive(pathname, to);
            return (
              <li key={to}>
                <Link
                  to={to}
                  aria-current={active ? 'page' : undefined}
                  className={`flex min-h-14 flex-col items-center justify-center gap-0.5 text-[0.6875rem] font-medium ${
                    active ? 'text-brand' : 'text-ink-faint'
                  }`}
                >
                  <Icon className="size-6" />
                  {label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}

function isActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname.startsWith(to);
}

/**
 * Tells the truth about connectivity rather than letting an edit look saved
 * when it is still sitting in a queue.
 */
function ConnectionBanner() {
  const online = useIsOnline();
  const pending = usePendingWrites();

  if (online && pending === 0) return null;

  return (
    <div
      role="status"
      className={`flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium ${
        online ? 'bg-brand-soft text-brand-ink' : 'bg-caution-soft text-caution'
      }`}
    >
      {online ? (
        <>
          <Spinner className="size-3" />
          Saving {pending} {pending === 1 ? 'change' : 'changes'} to your Sheet…
        </>
      ) : (
        <>
          <CloudOffIcon className="size-4" />
          Offline
          {pending > 0
            ? ` — ${pending} ${pending === 1 ? 'change' : 'changes'} will sync when you reconnect`
            : ' — showing your last synced data'}
        </>
      )}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="safe-top sticky top-0 z-30 border-b border-border bg-surface-raised/95 backdrop-blur">
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold tracking-tight">{title}</h1>
          {subtitle ? <p className="truncate text-xs text-ink-faint">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}

export function Page({ children }: { children: ReactNode }) {
  return <div className="mx-auto flex max-w-3xl flex-col gap-4 px-4 py-4">{children}</div>;
}

export function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-1">
      <h2 className="text-sm font-semibold tracking-tight text-ink">{title}</h2>
      {hint ? <p className="text-xs text-ink-faint">{hint}</p> : null}
    </div>
  );
}
